/**
 * Track 2: connects the game store to the audio facade – UI click ticks, fleet / build cues, alerts and the
 * music mood. Mounted once for the signed-in game; it only reads the store, no game code had to change.
 */
import { useEffect } from 'react';
import { useGameStore } from '../store/game-store.ts';
import { audio } from './index.ts';
import { fleetCues, isClickable, moodFor } from './cues.ts';

export function useAudioBridge(): void {
  useEffect(() => {
    const onClick = (e: MouseEvent) => { if (isClickable(e.target as Element | null)) audio.play('click'); };
    document.addEventListener('click', onClick, true);

    let prev = useGameStore.getState();
    audio.setMood(moodFor({ liveBattle: prev.liveBattle, conflicts: prev.conflicts, myCivId: prev.me?.civilization?.id }));
    const unsub = useGameStore.subscribe(next => {
      if (next.fleets !== prev.fleets) for (const c of fleetCues(prev.fleets, next.fleets)) audio.play(c);
      if (next.liveBattle && !prev.liveBattle) audio.play('alert');
      if (next.toasts.length > prev.toasts.length && next.toasts[next.toasts.length - 1]?.tone === 'error') audio.play('alert');
      if (next.battles.length > prev.battles.length) {
        const b = next.battles[0] ?? next.battles[next.battles.length - 1];
        const my = next.me?.civilization?.id;
        if (b?.winner && my) {
          const won = (b.attackerCivId === my && b.winner === 'attacker') || (b.defenderCivId === my && b.winner === 'defender');
          if (b.winner !== 'draw') audio.play(won ? 'victory' : 'defeat');
        }
      }
      if (next.liveBattle !== prev.liveBattle || next.conflicts !== prev.conflicts || next.me !== prev.me) {
        audio.setMood(moodFor({ liveBattle: next.liveBattle, conflicts: next.conflicts, myCivId: next.me?.civilization?.id }));
      }
      prev = next;
    });
    return () => { document.removeEventListener('click', onClick, true); unsub(); };
  }, []);
}
